import React, { useEffect, useState } from "react";
import SignaturePad from "react-signature-canvas";
import { AlertCircle, CheckCircle2, FileSignature, RotateCcw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export function PublicSepaSignaturePage({ token }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [signaturePad, setSignaturePad] = useState(null);

  useEffect(() => {
    loadRequest();
  }, [token]);

  const loadRequest = async () => {
    setLoading(true);
    setError("");

    try {
      const { data, error: functionError } = await supabase.functions.invoke(
        "get-sepa-signature-request",
        { body: { token } }
      );

      if (functionError) throw functionError;

      if (data?.status !== "pending") {
        setStatus(data?.status || "unavailable");
        setMessage(data?.message || "La solicitud no esta disponible");
        setRequestData(null);
        return;
      }

      setStatus("pending");
      setRequestData(data);
    } catch (err) {
      console.error("Error cargando solicitud SEPA:", err);
      setError(resolveFunctionError(err, "No se pudo cargar el consentimiento SEPA"));
    } finally {
      setLoading(false);
    }
  };

  const handleClearSignature = () => {
    signaturePad?.clear();
  };

  const handleSubmitSignature = async () => {
    if (!signaturePad || signaturePad.isEmpty()) {
      setError("La firma es obligatoria");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const signatureData = signaturePad.toDataURL("image/png");
      const { data, error: functionError } = await supabase.functions.invoke(
        "submit-sepa-signature",
        {
          body: {
            token,
            signatureData,
          },
        }
      );

      if (functionError) throw functionError;

      setStatus("signed");
      setMessage(`Consentimiento SEPA firmado correctamente${data?.signedAt ? ` el ${formatDateTime(data.signedAt)}` : ""}.`);
      setRequestData(null);
    } catch (err) {
      console.error("Error guardando firma SEPA:", err);
      setError(resolveFunctionError(err, "No se pudo guardar la firma"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
            <p className="mt-3 text-sm text-gray-600">Cargando consentimiento SEPA...</p>
          </div>
        </div>
      </PublicShell>
    );
  }

  if (status === "signed") {
    return (
      <PublicShell>
        <ResultMessage
          icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
          title="Consentimiento firmado"
          message={message || "El consentimiento SEPA ya ha sido firmado."}
        />
      </PublicShell>
    );
  }

  if (status && status !== "pending") {
    return (
      <PublicShell>
        <ResultMessage
          icon={<AlertCircle className="h-5 w-5 text-amber-600" />}
          title="Enlace no disponible"
          message={message || error || "El enlace de firma no esta disponible."}
        />
      </PublicShell>
    );
  }

  const customer = requestData?.customer || {};
  const company = requestData?.company || {};
  const creditorName = company.nombre_empresa || "Ennage, C.B.";

  return (
    <PublicShell>
      <div className="mx-auto max-w-4xl bg-white shadow-sm ring-1 ring-gray-200">
        <header className="border-b px-5 py-5 sm:px-8">
          <div className="flex items-start gap-3">
            <div className="mt-1 rounded bg-gray-900 p-2 text-white">
              <FileSignature className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-950">
                Orden de domiciliacion de adeudo directo SEPA
              </h1>
              <p className="mt-1 text-sm italic text-gray-600">SEPA Direct Debit Mandate</p>
            </div>
          </div>
        </header>

        <main className="space-y-6 px-5 py-6 sm:px-8">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <section className="space-y-3 border-b pb-5 text-sm">
            <MandateRow label="Referencia de la orden de domiciliacion" value={requestData?.mandateReference} translation="Mandate reference" />
            <MandateRow label="Identificador del acreedor" value={company.cif_empresa} translation="Creditor identifier" />
            <MandateRow label="Nombre del acreedor" value={creditorName} translation="Creditor's name" />
            <MandateRow label="Direccion" value={company.calle_empresa} translation="Address" />
            <MandateRow
              label="Codigo postal - Poblacion - Provincia"
              value={[company.poblacion_empresa, company.provincia_empresa].filter(Boolean).join(" ")}
              translation="Postal code - City - Town"
            />
            <MandateRow label="Pais" value="Espana" translation="Country" />
          </section>

          <section className="space-y-3 text-sm leading-6 text-gray-800">
            <p>
              Mediante la firma de esta orden de domiciliacion, el deudor autoriza (A) al acreedor a enviar instrucciones a la entidad del deudor para adeudar su cuenta y (B) a la entidad para efectuar los adeudos en su cuenta siguiendo las instrucciones del acreedor. Como parte de sus derechos, el deudor esta legitimado al reembolso por su entidad en los terminos y condiciones del contrato suscrito con la misma. La solicitud de reembolso debera efectuarse dentro de las ocho semanas que siguen a la fecha de adeudo en cuenta.
            </p>
            <p className="italic text-gray-600">
              By signing this mandate form, you authorise (A) the Creditor to send instructions to your bank to debit your account and (B) your bank to debit your account in accordance with the instructions from the Creditor. As part of your rights, you are entitled to a refund from your bank under the terms and conditions of your agreement with your bank. A refund must be claimed within eight weeks starting from the date on which your account was debited.
            </p>
          </section>

          <section className="space-y-3 border-t pt-5 text-sm">
            <MandateRow label="Nombre del deudor" value={customer.name} translation="Debtor's name" />
            <MandateRow label="Direccion del deudor" value={customer.street} translation="Address of the debtor" />
            <MandateRow
              label="Codigo postal - Poblacion - Provincia"
              value={[customer.postalCode, customer.city, customer.province].filter(Boolean).join(" ")}
              translation="Postal code - City - Town"
            />
            <MandateRow label="Pais del deudor" value="Espana" translation="Country of the debtor" />
            <MandateRow label="Numero de cuenta - IBAN" value={formatIBAN(customer.iban)} translation="Account number - IBAN" monospace />
            <MandateRow label="Tipo de pago" value="Pago recurrente" translation="Recurrent payment" />
            <MandateRow label="Fecha - Localidad" value={`${formatDate(new Date())} - ${displayValue(customer.city)}`} translation="Date - Location in which you are signing" />
          </section>

          <section className="space-y-3 border-t pt-5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Firma del deudor / Signature of the debtor</h2>
              <div className="mt-2 overflow-hidden border bg-white">
                <SignaturePad
                  ref={(ref) => setSignaturePad(ref)}
                  canvasProps={{
                    className: "h-44 w-full bg-white",
                  }}
                />
              </div>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:justify-between">
                <Button type="button" variant="outline" onClick={handleClearSignature} disabled={submitting}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Limpiar firma
                </Button>
                <Button type="button" onClick={handleSubmitSignature} disabled={submitting}>
                  <FileSignature className="mr-2 h-4 w-4" />
                  {submitting ? "Guardando..." : "Firmar consentimiento"}
                </Button>
              </div>
            </div>

            <div className="text-center text-xs font-medium leading-5 text-gray-700">
              <p>TODOS LOS CAMPOS HAN DE SER CUMPLIMENTADOS OBLIGATORIAMENTE.</p>
              <p>UNA VEZ FIRMADA, ESTA ORDEN DE DOMICILIACION DEBE SER ENVIADA AL ACREEDOR PARA SU CUSTODIA.</p>
              <p className="italic">ALL GAPS ARE MANDATORY. ONCE THIS MANDATE HAS BEEN SIGNED, MUST BE SENT TO CREDITOR FOR STORAGE.</p>
            </div>
          </section>
        </main>
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-gray-100 px-4 py-6 sm:px-6 sm:py-10">
      {children}
    </div>
  );
}

function ResultMessage({ icon, title, message }) {
  return (
    <div className="mx-auto max-w-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
      <div className="flex items-start gap-3">
        {icon}
        <div>
          <h1 className="text-lg font-semibold text-gray-950">{title}</h1>
          <p className="mt-2 text-sm leading-6 text-gray-700">{message}</p>
        </div>
      </div>
    </div>
  );
}

function MandateRow({ label, value, translation, monospace = false }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[240px_1fr] sm:gap-3">
      <div className="font-semibold text-gray-900">
        {label}: <span className="font-normal italic text-gray-500">/ {translation}</span>
      </div>
      <div className={monospace ? "font-mono text-gray-950" : "text-gray-800"}>
        {displayValue(value)}
      </div>
    </div>
  );
}

function displayValue(value) {
  return value?.toString().trim() ? value : "No informado";
}

function formatDate(date) {
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(value) {
  return new Date(value).toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatIBAN(iban) {
  if (!iban) return "";
  const cleaned = iban.replace(/\s/g, "");

  if (cleaned.length >= 24) {
    return [
      cleaned.slice(0, 4),
      cleaned.slice(4, 6),
      cleaned.slice(6, 10),
      cleaned.slice(10, 14),
      cleaned.slice(14, 16),
      cleaned.slice(16, 24),
    ].join(" ");
  }

  return [
    cleaned.slice(0, 4),
    cleaned.slice(4, 8),
    cleaned.slice(8, 10),
    cleaned.slice(10, 20),
  ].filter(Boolean).join(" ");
}

function resolveFunctionError(error, fallback) {
  if (!error) return fallback;
  if (typeof error.message === "string" && error.message) return error.message;
  if (typeof error.error === "string" && error.error) return error.error;
  return fallback;
}
